import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { autenticacao, ErroDaApi } from "@plantus/api-client";
import type { Perfil } from "@plantus/api-client";

/**
 * O estado de sessão do aplicativo.
 *
 * <h2>Onde a sessão NÃO está guardada</h2>
 *
 * Não há token aqui. Não há token em `localStorage`, `sessionStorage`,
 * cookie legível ou variável de módulo. A sessão é um cookie `HttpOnly`
 * que o navegador envia sozinho e que o JavaScript não consegue ler
 * (ADR-0008) — guardar qualquer cópia jogaria fora a proteção contra XSS
 * que é a razão inteira desse desenho.
 *
 * O que este provedor guarda é o **perfil** (nome, e-mail), que é dado de
 * exibição, e o estado do fluxo. Quem responde "estou autenticado?" é o
 * servidor, em `GET /api/auth/eu`.
 *
 * <h2>Por que "parcial" só existe em memória</h2>
 *
 * Entre a senha e o segundo fator, a sessão é parcial e alcança apenas
 * `/api/auth/mfa` — `GET /api/auth/eu` devolve 401 para ela, igual a uma
 * sessão inexistente. Recarregar a página nesse meio do caminho leva de
 * volta ao login, e está certo: meia credencial não deveria sobreviver a
 * um recarregamento.
 */
export type EstadoDaSessao =
  | { tipo: "verificando" }
  | { tipo: "anonimo" }
  | { tipo: "parcial"; nome: string }
  | { tipo: "autenticado"; perfil: Perfil };

interface Sessao {
  estado: EstadoDaSessao;
  entrar(identificador: string, senha: string, manterConectado: boolean): Promise<void>;
  confirmarCodigo(codigo: string): Promise<void>;
  sair(): Promise<void>;
}

const Contexto = createContext<Sessao | null>(null);

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [estado, definir] = useState<EstadoDaSessao>({ tipo: "verificando" });

  // Ao abrir o aplicativo, pergunta ao servidor. O cookie pode ter
  // sobrevivido a um recarregamento — ou pode ter expirado sem aviso.
  useEffect(() => {
    let ativo = true;
    autenticacao
      .eu()
      .then((perfil) => {
        if (ativo) definir({ tipo: "autenticado", perfil });
      })
      .catch(() => {
        if (ativo) definir({ tipo: "anonimo" });
      });
    return () => {
      ativo = false;
    };
  }, []);

  const entrar = useCallback(
    async (identificador: string, senha: string, manterConectado: boolean) => {
      const resposta = await autenticacao.entrar({ identificador, senha, manterConectado });

      if (resposta.segundoFatorPendente) {
        definir({ tipo: "parcial", nome: resposta.nome });
        return;
      }
      definir({ tipo: "autenticado", perfil: await autenticacao.eu() });
    },
    [],
  );

  const confirmarCodigo = useCallback(async (codigo: string) => {
    await autenticacao.confirmarSegundoFator(codigo);
    definir({ tipo: "autenticado", perfil: await autenticacao.eu() });
  }, []);

  const sair = useCallback(async () => {
    try {
      await autenticacao.sair();
    } catch (erro) {
      // Sessão já inválida no servidor é o desfecho que se queria: sair
      // não pode falhar do ponto de vista de quem clicou.
      if (!(erro instanceof ErroDaApi)) throw erro;
    }
    definir({ tipo: "anonimo" });
  }, []);

  const valor = useMemo<Sessao>(
    () => ({ estado, entrar, confirmarCodigo, sair }),
    [estado, entrar, confirmarCodigo, sair],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function usarSessao(): Sessao {
  const sessao = useContext(Contexto);
  if (sessao === null) {
    throw new Error("usarSessao precisa estar dentro de <ProvedorDeSessao>");
  }
  return sessao;
}
